import { Feature, Point } from 'geojson';
import { MapMouseEvent } from 'mapbox-gl';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useGroupHover from '@alltrails/core/hooks/useGroupHover';
import { SPACE_50 } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import { useAppSelector } from '../../redux';
import PopupOffset from '../../types/PopupOffset';
import { defaultSymbolLayerLayoutProps, getLayerBeforeId, photospotId } from '../../utils/layers';
import { serializedCoordinatesToCoordinates } from '../../utils/serializedCoordinates';
import photospotSvg, { svgSize } from '../../utils/svgDefinitions/photospotSvg';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import PaddingAwarePopup from '../PaddingAwarePopup';
import PhotospotPopupCard from './PhotospotPopupCard';
import type PhotospotResultProperties from './PhotospotResultProperties';

export const sourceId = 'photospot';
const svgDefinitions: SvgDefinition[] = [photospotSvg];

const popupOffset: PopupOffset = {
  bottom: [0, svgSize / -2 - SPACE_50],
  'bottom-left': [svgSize / -2, svgSize / -2 - SPACE_50],
  'bottom-right': [svgSize / 2, svgSize / -2 - SPACE_50],
  top: [0, svgSize / 2 + SPACE_50],
  'top-left': [svgSize / -2, svgSize / 2 + SPACE_50],
  'top-right': [svgSize / 2, svgSize / 2 + SPACE_50],
  left: [svgSize / 2 + SPACE_50, 0],
  right: [svgSize / -2 - SPACE_50, 0]
};

const Photospots = () => {
  const map = useMap();
  const { hasPermission } = useAuthorization();
  const { photospotResults } = useAppSelector(state => ({
    photospotResults: state.map.photospotResults
  }));
  const hasLocationsManagePermission = hasPermission({ permission: 'locations:manage' });

  const [hoveredPhotospot, setHoveredPhotospot] = useState<PhotospotResultProperties>();

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  const features = useMemo(() => {
    if (!photospotResults) {
      return [];
    }
    return Object.keys(photospotResults).reduce<Feature<Point, PhotospotResultProperties>[]>((allFeatures, serializedCoordinates) => {
      const photospot = photospotResults[serializedCoordinates];
      if (!photospot?._geoloc || !photospot.photos_count) {
        return allFeatures;
      }
      const { lat, lng } = serializedCoordinatesToCoordinates(serializedCoordinates);
      return [
        ...allFeatures,
        {
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {
            ...photospot,
            ID: photospot.ID,
            id: String(photospot.id),
            state_name: photospot.state_name,
            trails_count: photospot.trails_count,
            location_type: photospot.location_type,
            photo_count: photospot.photos_count,
            name: photospot.name,
            serializedCoordinates,
            latitude: lat,
            longitude: lng,
            type: 'photospot'
          },
          type: 'Feature'
        }
      ];
    }, []);
  }, [map, photospotResults]);

  const onHover = useCallback((photospot: PhotospotResultProperties) => {
    setHoveredPhotospot(photospot);
  }, []);

  const onHoverEnd = useCallback(() => {
    setHoveredPhotospot(undefined);
  }, []);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true);

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (onMouseEnter && event.features) {
        const spot = event.features[0].properties as PhotospotResultProperties;
        onMouseEnter(spot);
      }
    },
    [onMouseEnter]
  );

  useEffect(() => {
    registerEventHandler(photospotId, 'mousemove', onMouseMove);
    onMouseLeave && registerEventHandler(photospotId, 'mouseleave', onMouseLeave);
    return () => {
      unregisterEventHandler(photospotId, 'mousemove', onMouseMove);
      onMouseLeave && unregisterEventHandler(photospotId, 'mouseleave', onMouseLeave);
    };
  }, [onMouseLeave, onMouseEnter, onMouseMove, registerEventHandler, unregisterEventHandler]);

  const popup = useMemo(() => {
    if (!hoveredPhotospot) {
      return null;
    }

    const { latitude, longitude } = hoveredPhotospot;

    return (
      <PaddingAwarePopup
        closeButton={false}
        closeOnClick={false}
        focusAfterOpen={false}
        latitude={latitude}
        longitude={longitude}
        maxWidth="327"
        offset={popupOffset}
      >
        <PhotospotPopupCard
          photospot={hoveredPhotospot}
          onMouseEnter={() => onMouseEnter?.(hoveredPhotospot)}
          onMouseLeave={onMouseLeave}
          hasLocationsManagePermission={hasLocationsManagePermission}
        />
      </PaddingAwarePopup>
    );
  }, [hoveredPhotospot, onMouseEnter, onMouseLeave]);

  if (!map || !photospotResults || !imagesAreLoaded) {
    return null;
  }

  return (
    <>
      <Source id={sourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
        <Layer
          id={photospotId}
          source={sourceId}
          beforeId={getLayerBeforeId(map, photospotId)}
          type="symbol"
          layout={{
            ...defaultSymbolLayerLayoutProps,
            'icon-image': photospotSvg.name,
            'text-anchor': 'center',
            'text-field': ['get', 'order']
          }}
        />
      </Source>
      {popup}
    </>
  );
};

export default Photospots;
